/**
 * Public Hookah Lounge reservation flow.
 *
 * Reservations are free — no payment. The guest picks:
 *   1. Event day (Fri / Sat / Sun — June 19-21, 2026)
 *   2. Arrival slot (30-min starts)
 *   3. Party size
 *   4. Contact info (name required; email strongly recommended so the QR
 *      confirmation is delivered)
 *
 * After submit we route to /hookah-lounge-success?code=HK-XXXXXX&token=...
 * which renders the QR + summary the guest shows to the lounge staff.
 */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './HookahReservation.css';

const STEPS = ['Date', 'Time', 'Party', 'Details'];

const dayLabel = (iso) => {
  try {
    const d = new Date(iso + 'T12:00:00');
    const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    return { weekday, month, day: d.getDate() };
  } catch {
    return { weekday: iso, month: '', day: '' };
  }
};

const fmtTime = (hhmm) => {
  if (!hhmm) return '';
  try {
    return new Date(`2000-01-01T${hhmm}:00`).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return hhmm;
  }
};

const slotCanFitParty = (slot, partySize) => {
  const inventory = slot?.table_inventory || {};
  return Object.entries(inventory).some(([cap, row]) => Number(cap) >= Number(partySize) && Number(row?.remaining || 0) > 0);
};

const HookahReservation = () => {
  const navigate = useNavigate();

  // ── Config ─────────────────────────────────────────────────────
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState('');
  const [availability, setAvailability] = useState(null);

  // ── Form state ─────────────────────────────────────────────────
  const [step, setStep] = useState(0);
  const [eventDate, setEventDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const durationMinutes = Number(config?.default_duration_minutes || 120);
  const durationHours = (durationMinutes / 60).toFixed(durationMinutes % 60 === 0 ? 0 : 1);

  // Load config on mount
  useEffect(() => {
    (async () => {
      try {
        const resp = await api.hookahGetConfig();
        if (resp?.success) setConfig(resp.data);
      } catch (err) {
        setConfigError(err?.message || 'Failed to load lounge info. Try refreshing.');
      }
    })();
  }, []);

  // Load availability whenever the picked date changes
  useEffect(() => {
    if (!eventDate) { setAvailability(null); return; }
    setStartTime('');
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.hookahGetAvailability(eventDate);
        if (!cancelled && resp?.success) setAvailability(resp.data);
      } catch {
        if (!cancelled) setAvailability(null);
      }
    })();
    return () => { cancelled = true; };
  }, [eventDate]);


  // ── Navigation guards ──────────────────────────────────────────
  const canAdvance = () => {
    if (step === 0) return !!eventDate;
    if (step === 1) return !!startTime;
    if (step === 2) {
      const selectedSlot = (availability?.slots || []).find((s) => s.start_time === startTime);
      return partySize > 0 && !!selectedSlot && slotCanFitParty(selectedSlot, partySize);
    }
    if (step === 3) return !!customerName.trim() && (!!customerEmail.trim() || !!customerPhone.trim());
    return false;
  };

  const onNext = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const onBack = () => setStep((s) => Math.max(0, s - 1));

  const onSubmit = async () => {
    setSubmitError('');
    if (!canAdvance()) {
      setSubmitError('Please complete the form before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        event_date: eventDate,
        start_time: startTime,
        duration_minutes: config?.default_duration_minutes || 120,
        party_size: Number(partySize),
        customer_name: customerName.trim(),
        customer_email: customerEmail.trim(),
        customer_phone: customerPhone.trim(),
        notes: notes.trim(),
      };
      const resp = await api.hookahCreateReservation(payload);
      if (resp?.success) {
        const { reservation_code, qr_token } = resp.data || {};
        navigate(`/hookah-lounge-success?code=${encodeURIComponent(reservation_code)}&token=${encodeURIComponent(qr_token)}`);
        return;
      }
      setSubmitError(resp?.error || 'Could not create reservation.');
    } catch (err) {
      setSubmitError(err?.message || err?.error || 'Could not create reservation.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Early states ───────────────────────────────────────────────
  if (configError) {
    return (
      <div className="hk-root">
        <div className="hk-shell">
          <div className="hk-card">
            <div className="hk-alert">{configError}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="hk-root">
        <div className="hk-shell">
          <div className="hk-card">Loading lounge availability…</div>
        </div>
      </div>
    );
  }

  const eventDates = config.event_dates || [];
  const slots = availability?.slots || [];

  return (
    <div className="hk-root">
      <Helmet>
        <title>Hookah Lounge Reservation · OC MENA Festival</title>
        <meta name="description" content="Reserve your seat in the OC MENA Festival Hookah Lounge. Free reservations for Friday, Saturday, and Sunday (June 19–21, 2026)." />
      </Helmet>

      <div className="hk-shell">
        {/* Hero */}
        <div className="hk-hero">
          <span className="hk-hero-eyebrow">Hookah Lounge</span>
          <h1 className="hk-hero-title">Reserve your seat in the lounge</h1>
          <p className="hk-hero-sub">
            Free reservations for the OC MENA Festival Hookah Lounge.
            Pick a day, time slot, and party size — we&rsquo;ll email you a QR code
            to show our staff on arrival.
          </p>
        </div>

        {/* Progress */}
        <div className="hk-steps">
          {STEPS.map((label, i) => (
            <div
              key={label}
              className={`hk-step ${i === step ? 'is-active' : ''} ${i < step ? 'is-done' : ''}`}
            >
              {`0${i + 1}`.slice(-2)} · {label}
            </div>
          ))}
        </div>


        {submitError && <div className="hk-alert">{submitError}</div>}

        {/* Step 0 · Pick day */}
        {step === 0 && (
          <div className="hk-card">
            <h2 className="hk-section-title">Pick your day</h2>
            <div className="hk-tiles cols-3">
              {eventDates.map((iso) => {
                const d = dayLabel(iso);
                return (
                  <button
                    key={iso}
                    type="button"
                    className={`hk-tile ${eventDate === iso ? 'is-active' : ''}`}
                    onClick={() => { setEventDate(iso); }}
                  >
                    <small>{d.weekday}</small>
                    <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1 }}>{d.day}</div>
                    <small>{d.month}</small>
                  </button>
                );
              })}
            </div>
            <div className="hk-actions">
              <button className="hk-btn hk-btn-primary" disabled={!canAdvance()} onClick={onNext}>Continue</button>
            </div>
          </div>
        )}

        {/* Step 1 · Time slot */}
        {step === 1 && (
          <div className="hk-card">
            <h2 className="hk-section-title">Pick your time</h2>
            <p style={{ color: 'var(--hk-ink-muted)', fontSize: 13, margin: '0 0 14px' }}>
              30-minute slot starts from 11:00 AM to midnight. Each reservation is a fixed {durationHours}-hour session.
            </p>

            <div className="hk-tiles cols-4">
              {slots.map((slot) => {
                const unavailable = !slot.is_available;
                const totalRemaining = slot.remaining_tables ?? Object.values(slot.table_inventory || {}).reduce((sum, v) => sum + (v.remaining || 0), 0);
                return (
                  <button
                    key={slot.start_time}
                    type="button"
                    className={`hk-tile ${startTime === slot.start_time ? 'is-active' : ''} ${unavailable ? 'is-disabled' : ''}`}
                    onClick={() => !unavailable && setStartTime(slot.start_time)}
                    disabled={unavailable}
                  >
                    <div style={{ fontSize: 16, fontWeight: 800 }}>{fmtTime(slot.start_time)}</div>
                    <small>{unavailable ? 'Full' : `${totalRemaining} tables left`}</small>
                  </button>
                );
              })}
            </div>
            <div className="hk-actions">
              <button className="hk-btn hk-btn-ghost" onClick={onBack}>Back</button>
              <button className="hk-btn hk-btn-primary" disabled={!canAdvance()} onClick={onNext}>Continue</button>
            </div>
          </div>
        )}

        {/* Step 2 · Party size */}
        {step === 2 && (
          <div className="hk-card">
            <h2 className="hk-section-title">Party size</h2>
            <p style={{ color: 'var(--hk-ink-muted)', fontSize: 13, margin: '0 0 14px' }}>
              We auto-assign the best available table size for your party.
            </p>

            <div className="hk-field">
              <label className="hk-label" htmlFor="hk-party">How many guests?</label>
              <input
                id="hk-party"
                type="number"
                min={1}
                max={20}
                className="hk-input"
                value={partySize}
                onChange={(e) => setPartySize(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))}
              />
            </div>

            {startTime && slots.find((s) => s.start_time === startTime) && !slotCanFitParty(slots.find((s) => s.start_time === startTime), partySize) && (
              <div className="hk-alert" style={{ marginTop: 10 }}>
                This slot does not currently have inventory for {partySize} guests. Please go back and choose another slot.
              </div>
            )}

            <div className="hk-actions">
              <button className="hk-btn hk-btn-ghost" onClick={onBack}>Back</button>
              <button className="hk-btn hk-btn-primary" disabled={!canAdvance()} onClick={onNext}>Continue</button>
            </div>
          </div>
        )}

        {/* Step 3 · Contact + review */}
        {step === 3 && (
          <div className="hk-card">
            <h2 className="hk-section-title">Your details</h2>

            <div className="hk-field">
              <label className="hk-label" htmlFor="hk-name">Full name</label>
              <input
                id="hk-name"
                className="hk-input"
                placeholder="Jane Doe"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                required
              />
            </div>

            <div className="hk-field-row">
              <div className="hk-field">
                <label className="hk-label" htmlFor="hk-email">Email (for QR code)</label>
                <input
                  id="hk-email"
                  type="email"
                  className="hk-input"
                  placeholder="you@example.com"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                />
              </div>
              <div className="hk-field">
                <label className="hk-label" htmlFor="hk-phone">Phone</label>
                <input
                  id="hk-phone"
                  type="tel"
                  className="hk-input"
                  placeholder="(555) 123-4567"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                />
              </div>
            </div>

            <div className="hk-field">
              <label className="hk-label" htmlFor="hk-notes">Notes (optional)</label>
              <textarea
                id="hk-notes"
                className="hk-textarea"
                placeholder="Anything our staff should know?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="hk-review">
              <div className="hk-review-row">
                <span className="k">Session</span>
                <span className="v">{durationHours}-hour session ({durationMinutes} minutes)</span>
              </div>
              <div className="hk-review-row">
                <span className="k">Day</span>
                <span className="v">{eventDate && new Date(eventDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
              </div>
              <div className="hk-review-row">
                <span className="k">Time</span>
                <span className="v">{fmtTime(startTime)}</span>
              </div>
              <div className="hk-review-row">
                <span className="k">Party</span>
                <span className="v">{partySize} {partySize === 1 ? 'guest' : 'guests'}</span>
              </div>
            </div>

            <div className="hk-actions">
              <button className="hk-btn hk-btn-ghost" onClick={onBack} disabled={submitting}>Back</button>
              <button className="hk-btn hk-btn-primary" onClick={onSubmit} disabled={submitting || !canAdvance()}>
                {submitting ? 'Confirming…' : 'Confirm reservation'}
              </button>
            </div>
          </div>
        )}

        <p className="hk-footer-note">
          The OC MENA Festival Hookah Lounge is open <strong>11:00 AM – 12:00 AM</strong>
          on <strong>Friday, Saturday &amp; Sunday · June 19–21, 2026</strong>.
          Reservations are free and limited to the lounge&rsquo;s {config.max_capacity}-guest capacity.
        </p>
      </div>
    </div>
  );
};

export default HookahReservation;
